import React, { useState, useMemo, useCallback } from "react";
import { Calculator, ArrowRight, Sparkles, Info } from "lucide-react";
import {
  RESIDENTIAL_PLANS,
  findTierForSqft,
  recommendPlanForSqft,
} from "@/data/maintenancePlans";

/**
 * Friendly "what this means" copy for each plan id. Kept inline so the
 * recommender stays plain JS and the explanations are easy to tune.
 */
const PLAN_EXPLANATIONS = {
  "roofwatch-residential":
    "RoofWatch Residential focuses on roofline checks, storm follow-up priority, and roof-related documentation. It's a lean roof-only membership for homeowners who mainly want eyes on the roof.",
  "homecare-essential":
    "HomeCare Essential gives you basic seasonal maintenance visits, documentation, and member pricing without a higher monthly commitment. Best when your home is in solid shape and you want light recurring support.",
  "homecare-plus":
    "HomeCare Plus is the recommended default. Quarterly visits, priority scheduling, a stronger annual covered-work allowance, structured interior and exterior checks, and included crew hours per approved claim.",
  "homecare-reserve":
    "HomeCare Reserve is the strongest residential plan: quarterly visits, roof review, the largest annual allowance, more included crew hours per approved claim, and stronger documentation.",
};

function ResultRow({ label, value, mono = true }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">{label}</div>
      <div className={`text-sm font-semibold text-gray-900 text-right ${mono ? "tabular-nums" : ""}`}>{value}</div>
    </div>
  );
}

function RecommendedPlanCard({ plan, tier, label, onSelect, isCustom }) {
  if (!plan) return null;
  const tierCell = tier?.plans?.[plan.id];
  const priceLabel = tierCell?.priceLabel || `Starting at $${plan.price}/mo`;
  const crewHours = tierCell?.crewHours || "Custom";
  const explanation = PLAN_EXPLANATIONS[plan.id] || plan.bestFor;

  return (
    <div
      className={`border-2 rounded-lg p-5 flex flex-col gap-3 ${plan.highlight ? "border-br-bronze bg-white" : "border-gray-200 bg-white"}`}
      data-testid={`recommender-card-${plan.id}`}
    >
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-br-bronze font-bold flex items-center gap-1">
          <Sparkles size={10} /> {label}
        </div>
        <div className="font-display text-lg tracking-widest uppercase mt-1 text-br-black">{plan.name}</div>
      </div>

      <div className="divide-y divide-gray-100 border-y border-gray-100">
        <ResultRow label="Estimated Monthly" value={priceLabel} />
        <ResultRow label="Initial Inspection Fee" value={tier?.inspectionFeeLabel || "Custom quote required"} />
        <ResultRow label="Included Crew Hours / Claim" value={`${crewHours}${isCustom ? "" : " per approved claim"}`} mono={false} />
        <ResultRow label="Annual Covered-Work Allowance" value={plan.allowance} mono={false} />
      </div>

      <p className="text-xs text-gray-700 leading-relaxed">{explanation}</p>

      {onSelect && (
        <button
          type="button"
          onClick={() => onSelect(plan)}
          className={`${plan.highlight ? "br-btn-primary" : "br-btn-dark"} justify-center mt-1`}
          data-testid={`recommender-select-${plan.id}`}
        >
          Use This Plan <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
}

/**
 * PlanRecommender
 *
 * Calculator that asks the homeowner for their approximate square footage,
 * resolves the matching square-footage tier, and surfaces one or two
 * recommended residential plans with the full transparency block: monthly
 * price, initial inspection fee, included crew hours per covered claim, and
 * annual covered-work allowance.
 *
 * Calls `onApply({ plan, sqft, tier })` when the homeowner picks a plan, so
 * the parent page can scroll to the request form and prefill it.
 */
export default function PlanRecommender({ onApply }) {
  const [input, setInput] = useState("");
  const [submittedSqft, setSubmittedSqft] = useState(null);

  const result = useMemo(() => {
    if (submittedSqft === null) return null;
    const rec = recommendPlanForSqft(submittedSqft);
    if (!rec) return null;
    const tier = findTierForSqft(submittedSqft);
    const primary = RESIDENTIAL_PLANS.find((p) => p.id === rec.primaryId) || null;
    const secondary = rec.secondaryId ? RESIDENTIAL_PLANS.find((p) => p.id === rec.secondaryId) : null;
    return { rec, tier, primary, secondary };
  }, [submittedSqft]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const n = parseInt(input.replace(/[^0-9]/g, ""), 10);
    if (Number.isFinite(n) && n > 0) setSubmittedSqft(n);
    else setSubmittedSqft(null);
  }, [input]);

  const handleReset = () => {
    setInput("");
    setSubmittedSqft(null);
  };

  const handleSelectPlan = useCallback((plan) => {
    if (typeof onApply === "function" && result?.tier) {
      onApply({ plan, sqft: submittedSqft, tier: result.tier });
    } else {
      const el = document.getElementById("request-details");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [onApply, submittedSqft, result]);

  return (
    <div className="bg-br-black text-white rounded-lg overflow-hidden border border-br-line" data-testid="plan-recommender" id="calculator">
      <div className="px-6 py-5 border-b border-br-line">
        <div className="flex items-center gap-2">
          <Calculator size={18} className="text-br-bronze-2" />
          <div className="br-eyebrow text-br-bronze-2">Find Your Plan</div>
        </div>
        <h3 className="font-display text-2xl tracking-widest uppercase mt-2">Estimate Your Plan in 5 Seconds</h3>
        <p className="text-sm text-gray-300 mt-2 max-w-2xl">
          Enter your home&rsquo;s approximate square footage to see your tier, recommended plan, starting price, initial inspection fee, and included crew-hour limit. Final pricing is confirmed after a property review.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-5 grid sm:grid-cols-[1fr_auto] gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-1" htmlFor="recommender-sqft">Home Square Footage</label>
          <input
            id="recommender-sqft"
            type="number"
            min="100"
            max="20000"
            inputMode="numeric"
            placeholder="e.g. 2100"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="w-full bg-br-charcoal border border-br-line rounded px-4 py-3 text-base text-white placeholder:text-gray-500 focus:border-br-bronze-2 focus:outline-none"
            data-testid="recommender-sqft-input"
          />
        </div>
        <div className="flex flex-col gap-2 sm:items-end sm:justify-end">
          <button type="submit" className="br-btn-primary justify-center" data-testid="recommender-submit">
            See My Plan <ArrowRight size={14} />
          </button>
          {submittedSqft !== null && (
            <button
              type="button"
              onClick={handleReset}
              className="text-[10px] uppercase tracking-widest text-gray-400 hover:text-br-bronze-2"
              data-testid="recommender-reset"
            >
              Reset
            </button>
          )}
        </div>
      </form>

      {result && (
        <div className="px-6 py-5 border-t border-br-line bg-br-charcoal" data-testid="recommender-result">
          <div className="flex flex-wrap items-baseline gap-2 mb-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-br-bronze-2 font-bold">Tier</div>
            <div className="font-display text-lg tracking-widest uppercase">{result.tier?.label}</div>
            <div className="text-xs text-gray-400">&middot; {result.tier?.range}</div>
          </div>

          <div className="flex items-start gap-2 bg-br-black/40 border border-br-line rounded p-3 mb-4">
            <Info size={14} className="text-br-bronze-2 shrink-0 mt-0.5" />
            <p className="text-sm text-gray-300 leading-relaxed">{result.rec.message}</p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <RecommendedPlanCard
              plan={result.primary}
              tier={result.tier}
              label="Recommended"
              onSelect={handleSelectPlan}
              isCustom={!!result.tier?.isCustom}
            />
            {result.secondary && (
              <RecommendedPlanCard
                plan={result.secondary}
                tier={result.tier}
                label="Alternative"
                onSelect={handleSelectPlan}
                isCustom={!!result.tier?.isCustom}
              />
            )}
          </div>

          <p className="mt-4 text-[11px] text-gray-400 italic leading-relaxed">
            Pricing shown is a starting estimate. Final pricing can change based on property condition, access, deferred maintenance, roof height, service area, and known repair issues.
          </p>
        </div>
      )}
    </div>
  );
}
